if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        // Listen to variant change events for Swiper gallery updates
        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.handleVariantChangeForGallery.bind(this)
        );

        // Track last modified select picker for bundle products
        this.lastModifiedSelect = null;
        this.trackBundleSelectChanges();

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.variantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        /**/
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id, variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          const syncAllPriceRoots = () => {
            const priceId = `price-${this.sectionId}`;
            const source = html.getElementById(priceId);
            if (!source) return;
            const hide = false;
            const destSection = this.dataset.section;
            const ids = new Set([`price-${this.sectionId}`, `price-${destSection}`]);
            ids.forEach((fullId) => {
              const esc = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(fullId) : fullId;
              this.querySelectorAll(`[id="${esc}"]`).forEach((destination) => {
                destination.innerHTML = source.innerHTML;
                destination.classList.toggle('hidden', hide);
              });
            });
          };
          syncAllPriceRoots();
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateVariantSpecificContentFromDoc(html);
          this.updateVariantSpecialImagesFromVariant(variant);

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules'].forEach((idBase) => {
          const fullId = `${idBase}-${this.dataset.section}`;
          const esc = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(fullId) : fullId;
          this.querySelectorAll(`[id="${esc}"]`).forEach(({ classList }) => classList.add('hidden'));
        });
      }

      updateMedia(html, variantFeaturedMediaId, variant = null) {
        console.log('[Gallery] updateMedia called — variantFeaturedMediaId:', variantFeaturedMediaId, '| variant:', variant?.title);
        const mediaGallery = this.querySelector('media-gallery');
        const isSwiperGallery = mediaGallery?.dataset.swiperGallery === 'true';
        console.log('[Gallery] isSwiperGallery:', isSwiperGallery);

        if (isSwiperGallery) {
          // Swiper galleries must always reinitialize from fetched HTML so the
          // correct variant images are present, even when the variant has no
          // featured_media (images not associated in Shopify admin).
          this.reinitializeSwiperGallery(html, variant);
        } else {
          if (!variantFeaturedMediaId) return;
          // Legacy gallery handling
        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
          mediaGallery?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

          // Filter media by alt attribute when hide_variants is enabled
          if (this.dataset.hideVariants === 'true' || this.querySelector('[data-hide-variants]')) {
            this.filterMediaByAlt(variant);
          }
        }

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      /**
       * Gallery filtering only runs when at least one image alt contains "gallery".
       * Otherwise all media stays visible for every variant / option / pack.
       */
      hasGalleryAltConvention(mediaGallery) {
        if (!mediaGallery) return false;
        const imgs = mediaGallery.querySelectorAll('img[alt]');
        for (const img of imgs) {
          const a = (img.getAttribute('alt') || '').toLowerCase();
          if (a.includes('gallery')) return true;
        }
        return false;
      }

      withGalleryTransition(mediaGallery, updateFn) {
        if (!mediaGallery) {
          updateFn();
          return;
        }

        const wrapper = mediaGallery.querySelector('.product-gallery-swiper-wrapper');
        if (!wrapper) {
          updateFn();
          return;
        }

        mediaGallery.classList.add('is-gallery-updating');
        void wrapper.offsetWidth;

        window.setTimeout(() => {
          const done = updateFn();
          const finish = () => {
            requestAnimationFrame(() => {
              mediaGallery.classList.remove('is-gallery-updating');
            });
          };

          if (done && typeof done.then === 'function') {
            done.then(finish);
          } else {
            finish();
          }
        }, 180);
      }

      orderGallerySlides(slides, variant, sectionId, slideType = 'main') {
        const expectedAlt = `${variant.title} Gallery`;
        const optionValues = Array.isArray(variant.options) ? variant.options : [];
        const optionAlts = optionValues.map((opt) => `${opt} Gallery`);
        const variantMediaId = variant.featured_media?.id;
        const variantSlideId = variantMediaId ? `${sectionId}-${variantMediaId}` : null;

        const fullMatchSlides = [];
        const perOptionSlides = optionValues.map(() => []);
        const packTaggedSlides = [];
        const untaggedSlides = [];

        const getSlideKey = (slide) =>
          slideType === 'thumbnail' ? slide.dataset.target : slide.dataset.mediaId;

        slides.forEach((slide) => {
          const slideKey = getSlideKey(slide);
          const img = slide.querySelector('img[alt]');
          const imgAlt = img ? img.getAttribute('alt') || '' : '';
          const altLower = imgAlt.toLowerCase();

          if (slideKey === variantSlideId) {
            fullMatchSlides.push(slide);
            return;
          }

          if (!img) {
            untaggedSlides.push(slide);
            return;
          }

          if (!altLower.includes('gallery')) {
            untaggedSlides.push(slide);
            return;
          }

          if (imgAlt === expectedAlt) {
            fullMatchSlides.push(slide);
            return;
          }

          const optIdx = optionAlts.indexOf(imgAlt);
          if (optIdx !== -1) {
            perOptionSlides[optIdx].push(slide);
            return;
          }

          if (altLower.includes('single ') || altLower.includes('-pack ')) {
            packTaggedSlides.push(slide);
          }
        });

        const orderedSlides = [];
        const featuredSlide = fullMatchSlides.find((s) => getSlideKey(s) === variantSlideId);
        if (featuredSlide) orderedSlides.push(featuredSlide);
        fullMatchSlides.forEach((s) => {
          if (s !== featuredSlide) orderedSlides.push(s);
        });
        perOptionSlides.forEach((bucket) => bucket.forEach((s) => orderedSlides.push(s)));
        packTaggedSlides.forEach((s) => orderedSlides.push(s));
        untaggedSlides.forEach((s) => orderedSlides.push(s));

        return orderedSlides;
      }

      applyOrderedSlidesInPlace(wrapper, orderedSlides) {
        if (!wrapper || orderedSlides.length === 0) return;
        orderedSlides.forEach((slide) => slide.remove());
        orderedSlides.forEach((slide) => wrapper.appendChild(slide));
      }

      replaceWrapperSlides(wrapper, orderedSlides) {
        if (!wrapper) return;
        wrapper.innerHTML = '';
        orderedSlides.forEach((slide) => {
          wrapper.appendChild(slide.cloneNode(true));
        });
      }

      reinitializeGallerySwipers(mediaGallery) {
        const sourceViewer = mediaGallery.querySelector('[data-gallery-viewer="true"]');
        const sourceThumbnails = mediaGallery.querySelector('[data-gallery-thumbnails="true"]');

        const slideToFirst = (slider) => {
          requestAnimationFrame(() => {
            if (slider?.swiperInstance?.slides?.length) {
              slider.swiperInstance.slideTo(0, 0);
            }
          });
        };

        return new Promise((resolve) => {
          const finish = () => window.setTimeout(resolve, 30);

          if (sourceThumbnails?.reinitialize) {
            sourceThumbnails.reinitialize();

            const reconnectMain = () => {
              if (sourceThumbnails.swiperInstance && sourceViewer?.reinitialize) {
                sourceViewer.reinitialize();
                slideToFirst(sourceThumbnails);
                slideToFirst(sourceViewer);
                finish();
              } else {
                setTimeout(reconnectMain, 50);
              }
            };

            setTimeout(reconnectMain, 50);
          } else if (sourceViewer?.reinitialize) {
            sourceViewer.reinitialize();
            slideToFirst(sourceViewer);
            finish();
          } else {
            resolve();
          }
        });
      }

      filterMediaByAlt(variant) {
        if (!variant) {
          variant = this.getSelectedVariant(this);
        }
        
        if (!variant || !variant.title) {
          console.log('[Gallery] filterMediaByAlt: no variant, skipping');
          return;
        }

        const mediaGallery = this.querySelector('media-gallery');
        if (!mediaGallery) return;

        if (!this.hasGalleryAltConvention(mediaGallery)) {
          console.log('[Gallery] filterMediaByAlt: no gallery alt convention found, skipping filter');
          return;
        }

        const expectedAlt = `${variant.title} Gallery`;
        console.log('[Gallery] filterMediaByAlt — variant:', variant.title, '| expectedAlt:', expectedAlt, '| featured_media id:', variant.featured_media?.id);

        // Check if this is a Swiper gallery
        const isSwiperGallery = mediaGallery.dataset.swiperGallery === 'true';
        
        if (isSwiperGallery) {
          this.filterSwiperGallery(mediaGallery, variant);
        } else {
          // Legacy gallery (non-Swiper) - filter li elements
          this.filterLegacyGallery(mediaGallery, expectedAlt, variant);
        }
      }

      filterSwiperGallery(mediaGallery, variant) {
        const mainViewer = mediaGallery.querySelector('[data-gallery-viewer="true"]');
        if (!mainViewer) return;

        const wrapper = mainViewer.querySelector('.swiper-wrapper');
        if (!wrapper) return;

        const allSlides = Array.from(wrapper.querySelectorAll('.swiper-slide[data-media-id]'));
        const orderedSlides = this.orderGallerySlides(allSlides, variant, this.dataset.section, 'main');
        if (orderedSlides.length === 0) return;

        const thumbnailsSwiper = mediaGallery.querySelector('[data-gallery-thumbnails="true"]');
        let orderedThumbs = [];
        let thumbnailWrapper = null;

        if (thumbnailsSwiper) {
          thumbnailWrapper = thumbnailsSwiper.querySelector('.swiper-wrapper');
          if (thumbnailWrapper) {
            const thumbSlides = Array.from(
              thumbnailWrapper.querySelectorAll('.swiper-slide.thumbnail-list__item')
            );
            orderedThumbs = this.orderGallerySlides(thumbSlides, variant, this.dataset.section, 'thumbnail');
          }
        }

        this.withGalleryTransition(mediaGallery, () => {
          this.applyOrderedSlidesInPlace(wrapper, orderedSlides);

          if (thumbnailWrapper && orderedThumbs.length > 0) {
            this.applyOrderedSlidesInPlace(thumbnailWrapper, orderedThumbs);
          }

          return this.reinitializeGallerySwipers(mediaGallery);
        });
      }

      reinitializeSwiperGallery(html, variant = null) {
        const mediaGallery = this.querySelector('media-gallery');
        if (!mediaGallery) return;

        const mediaGalleryDestination = html.querySelector('media-gallery');
        if (!mediaGalleryDestination) return;

        const destinationViewer = mediaGalleryDestination.querySelector('[data-gallery-viewer="true"]');
        const sourceViewer = mediaGallery.querySelector('[data-gallery-viewer="true"]');
        if (!destinationViewer || !sourceViewer) return;

        const destinationWrapper = destinationViewer.querySelector('.swiper-wrapper');
        const sourceWrapper = sourceViewer.querySelector('.swiper-wrapper');
        if (!destinationWrapper || !sourceWrapper) return;

        let mainSlides = Array.from(destinationWrapper.querySelectorAll('.swiper-slide[data-media-id]'));
        if (variant && this.hasGalleryAltConvention(mediaGallery)) {
          mainSlides = this.orderGallerySlides(mainSlides, variant, this.dataset.section, 'main');
        }

        const destinationThumbnails = mediaGalleryDestination.querySelector('[data-gallery-thumbnails="true"]');
        const sourceThumbnails = mediaGallery.querySelector('[data-gallery-thumbnails="true"]');
        let thumbSlides = [];
        let sourceThumbWrapper = null;

        if (destinationThumbnails && sourceThumbnails) {
          const destinationThumbWrapper = destinationThumbnails.querySelector('.swiper-wrapper');
          sourceThumbWrapper = sourceThumbnails.querySelector('.swiper-wrapper');
          if (destinationThumbWrapper) {
            thumbSlides = Array.from(
              destinationThumbWrapper.querySelectorAll('.swiper-slide.thumbnail-list__item')
            );
            if (variant && this.hasGalleryAltConvention(mediaGallery)) {
              thumbSlides = this.orderGallerySlides(thumbSlides, variant, this.dataset.section, 'thumbnail');
            }
          }
        }

        const finalMainSlides = mainSlides;
        const finalThumbSlides = thumbSlides;

        this.withGalleryTransition(mediaGallery, () => {
          this.replaceWrapperSlides(sourceWrapper, finalMainSlides);

          if (sourceThumbWrapper && finalThumbSlides.length > 0) {
            this.replaceWrapperSlides(sourceThumbWrapper, finalThumbSlides);
          }

          return this.reinitializeGallerySwipers(mediaGallery);
        });
      }

      filterLegacyGallery(mediaGallery, expectedAlt, variant = null) {
        const expectedAlts = new Set([expectedAlt]);
        if (variant && Array.isArray(variant.options)) {
          variant.options.forEach((opt) => expectedAlts.add(`${opt} Gallery`));
        }

        const filterItems = (items) => {
          items.forEach((item) => {
            const img = item.querySelector('img[alt]');

            // No img or no alt → untagged, always visible
            if (!img) {
              item.style.display = '';
              item.classList.remove('hidden');
              return;
            }

            const imgAlt = img.getAttribute('alt') || '';
            const altLower = imgAlt.toLowerCase();

            // Untagged or pack-tagged → always visible
            if (!altLower.includes('gallery') || altLower.includes('single ') || altLower.includes('-pack ')) {
              item.style.display = '';
              item.classList.remove('hidden');
              return;
            }

            // Variant-gallery match → visible; other variant gallery → hidden
            if (expectedAlts.has(imgAlt)) {
              item.style.display = '';
              item.classList.remove('hidden');
            } else {
              item.style.display = 'none';
              item.classList.add('hidden');
            }
          });
        };

        filterItems(mediaGallery.querySelectorAll('li.product__media-item'));
        filterItems(mediaGallery.querySelectorAll('li.thumbnail-list__item'));
      }

      updateVariantSpecialImagesFromVariant(variant) {
        if (!variant?.id) return;

        document.querySelectorAll('[data-variant-special-image]').forEach((container) => {
          const jsonId = container.dataset.variantSpecialImagesId;
          const jsonEl = jsonId ? document.getElementById(jsonId) : null;
          if (!jsonEl) return;

          let data;
          try {
            data = JSON.parse(jsonEl.textContent);
          } catch {
            return;
          }

          const variantImage = data.variants?.[String(variant.id)];
          const imageData = variantImage || data.fallback;
          if (!imageData?.src) return;

          const media = container.querySelector('.collapsible-content__media');
          const img = media?.querySelector('img');
          if (!img) return;

          img.src = imageData.src;
          img.alt = imageData.alt || '';
          img.removeAttribute('srcset');
          img.removeAttribute('width');
          img.removeAttribute('height');

          if (media && container.dataset.imageRatio === 'adapt' && imageData.aspectRatio) {
            media.style.paddingBottom = `${(1 / Number(imageData.aspectRatio)) * 100}%`;
          }
        });
      }

      updateVariantSpecificContentFromDoc(doc) {
        if (!doc) return;

        this.querySelectorAll('[data-variant-specific-accordion]').forEach((accordion) => {
          const source =
            doc.getElementById(accordion.id) ||
            doc.querySelector(`[id="${accordion.id}"]`);
          if (source) accordion.innerHTML = source.innerHTML;
        });

        this.updateVariantSpecialImagesFromDoc(doc);
      }

      updateVariantSpecialImagesFromDoc(doc) {
        if (!doc) return;

        document.querySelectorAll('[data-variant-special-image]').forEach((container) => {
          const source =
            doc.getElementById(container.id) ||
            doc.querySelector(`[id="${container.id}"]`);
          if (source) container.innerHTML = source.innerHTML;
        });
      }

      handleVariantChangeForGallery(event) {
        console.log('[Gallery] handleVariantChangeForGallery — sectionId match:', event.data?.sectionId, '===', this.sectionId, '| variant:', event.data?.variant?.title, '| featured_media:', event.data?.variant?.featured_media?.id);
        // Only handle if this is for our section
        if (String(event.data?.sectionId) !== String(this.sectionId)) return;

        const variant = event.data?.variant;
        if (!variant) return;

        this.updateVariantSpecialImagesFromVariant(variant);

        // Update variant-specific accordions by fetching full product page with this variant
        const accordions = this.querySelectorAll('[data-variant-specific-accordion]');
        if (accordions.length > 0) {
          const productUrl = this.dataset.url;
          if (productUrl) {
            const variantUrl = `${productUrl}${productUrl.includes('?') ? '&' : '?'}variant=${variant.id}`;
            fetch(variantUrl, { headers: { Accept: 'text/html' } })
              .then((res) => res.text())
              .then((text) => {
                const doc = new DOMParser().parseFromString(text, 'text/html');
                this.updateVariantSpecificContentFromDoc(doc);
              })
              .catch(() => {});
          }
        }

        const mediaGallery = this.querySelector('media-gallery');
        if (!mediaGallery || mediaGallery.dataset.swiperGallery !== 'true') return;

        // For bundle products, only update if this variant change came from the last modified select
        if (this.dataset.bundlePack === 'true' && this.lastModifiedSelect) {
          // Check if the variant change originated from the last modified select
          const variantIdInput = this.querySelector('input[name="id"]');
          if (variantIdInput) {
            // Get the variant from the last modified select
            const lastSelect = this.lastModifiedSelect;
            const selectedOption = lastSelect.options[lastSelect.selectedIndex];
            const selectVariantId = selectedOption?.dataset?.variantid;
            
            // Only update gallery if this variant matches the last modified select
            if (selectVariantId && variant.id.toString() !== selectVariantId) {
              return;
            }
          }
        }
      }

      trackBundleSelectChanges() {
        // Only track if this is a bundle product
        if (this.dataset.bundlePack !== 'true') return;

        // Find all flavor select pickers
        const flavorSelects = this.querySelectorAll('select[data-pack-key][data-flavor-index]');
        
        flavorSelects.forEach((select) => {
          select.addEventListener('change', (e) => {
            // Track the last modified select
            this.lastModifiedSelect = e.target;
            
            // Get the variant from the select
            const selectedOption = e.target.options[e.target.selectedIndex];
            const variantId = selectedOption?.dataset?.variantid;
            
            if (variantId) {
              // Try to get variant from the pack product
              const packKey = e.target.dataset.packKey;
              const packForm = this.querySelector('pack-product-form');
              
              if (packForm && packForm.currentProduct) {
                const variant = packForm.currentProduct.variants?.find(v => v.id.toString() === variantId);
                if (variant) {
                  // Update gallery immediately
                  setTimeout(() => {
                    this.filterMediaByAlt(variant);
                  }, 100);
                }
              } else {
                // Fallback: try to get from product data script
                const productData = this.querySelector('script[type="application/json"][data-product]');
                if (productData) {
                  try {
                    const product = JSON.parse(productData.textContent);
                    const variant = product.variants?.find(v => v.id.toString() === variantId);
                    if (variant) {
                      setTimeout(() => {
                        this.filterMediaByAlt(variant);
                      }, 100);
                    }
                  } catch (error) {
                    console.error('Error parsing product data:', error);
                  }
                }
              }
            }
          });
        });
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;
        
        if (!this.querySelector('.quantity__rules-cart .loading__spinner')) return;

        this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
